import { useEffect, useState, FC } from 'react';
import { Box, DialogTitle, styled } from '@mui/material';
import { Form } from '@chargeafter/forms';
import {
  mobileValidationRegexByCurrency,
  sendLinkFormSchema,
} from '../../validations';
import {
  LinkFlow,
  LinkDeliveryMethod,
  LinkInteraction,
  useGetLinkInteraction,
  useResetLinkInteraction,
  useSendLink,
  useSetLinkInteraction,
} from '../../services/sendLinkManager';
import { useGetConsumer, useStartOver } from '../../services/flowManager';
import logger from '../../services/loggerManager';
import { useGetUserPermissions } from '../../services/userManager';
import {
  useGetInteractionOptions,
  useGetMerchantConfigurationOptions,
  useGetMerchantCurrency,
  useGetMerchantLinkExpirations,
  useGetSelectedSettleOption,
} from '../../services/configManager';
import { ButtonName, ESendLinkModal } from '../../constants';
import { sendButtonClickEvent } from '../../services/trackerManager';
import {
  useResetCurrentModal,
  useSetCurrentModal,
} from '../../services/modalManager';
import { ExpiredLink } from './ExpiredLink';
import { LinkSent } from './LinkSent';
import { ConsumerLinkDetails } from './ConsumerDetailsFormContent';
import { QrCodeScreen } from './QrCodeScreen';
import ToggleGroup from '../../components/ToggleGroup';
import { VerticalSpace } from '@chargeafter/ui';

enum ELinkModalStep {
  GetDetails,
  ViewQr,
  ExpiredLink,
  LinkSent,
}

export type LinkError = {
  code: string;
  message: string | JSX.Element;
} | null;

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  height: theme.spacing(7),
  backgroundColor: theme.palette.secondary.light,
  marginBottom: theme.spacing(2),
}));

const ToggleWrapper = styled(Box)({
  width: '80%',
  margin: '0 auto',
});

type LinkModalProps = {
  updateClose: (func: () => void) => void;
  close: () => void;
};

const LinkModal: FC<LinkModalProps> = ({ updateClose, close }) => {
  const [step, setStep] = useState(ELinkModalStep.GetDetails);
  const [linkUrl, setLinkUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Record<string, any> | null>(null);
  const linkInteraction = useGetLinkInteraction();
  const isCheckout = linkInteraction.action !== LinkFlow.Apply;
  const isQrCode = linkInteraction.deliveryMethod === LinkDeliveryMethod.QrCode;
  const updateLinkInteraction = useSetLinkInteraction();
  const userPermissions = useGetUserPermissions();
  const consumerDetails = useGetConsumer();
  const selectedSettleOption = useGetSelectedSettleOption();
  const linkExpirationInSeconds = useGetMerchantLinkExpirations()?.Url ?? 300;
  const [timeLeftInSeconds, setTimeLeftInSeconds] = useState(
    linkExpirationInSeconds,
  );
  const [sentSuccessfully, setSentSuccessfully] = useState(false);
  const sendLink = useSendLink();
  const updateCurrentModal = useSetCurrentModal();
  const resetCurrentModal = useResetCurrentModal();
  const startOver = useStartOver();
  const resetLinkInteraction = useResetLinkInteraction();
  const merchantInteractionOptions = useGetInteractionOptions();
  const currency = useGetMerchantCurrency();
  const { citiExperience } = useGetMerchantConfigurationOptions();
  const interactionOptions = getInteractionOptions(
    merchantInteractionOptions,
    linkInteraction.action,
  );
  const [error, setError] = useState<LinkError>(null);
  const updateError = (v: LinkError) => setError(v);

  const updateSelectedDeliveryMethod = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value: string | null,
  ) => {
    value &&
      updateLinkInteraction({
        ...linkInteraction,
        deliveryMethod: value as unknown as LinkDeliveryMethod,
      });
  };

  useEffect(() => {
    updateClose(() => () => {
      resetCurrentModal();
      resetLinkInteraction();
      logger.log(`Closed send link modal`, {
        filename: 'SendLinkModal.tsx',
        function: 'closeModal',
      });
    });
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timer | null = null;

    const onSecondPassed = () => {
      setTimeLeftInSeconds((prev) => {
        if (prev > 1) return prev - 1;
        setStep(ELinkModalStep.ExpiredLink);
        return 0;
      });
    };

    if (step === ELinkModalStep.ViewQr) {
      timer = setInterval(onSecondPassed, 1000);
    } else if (timer) clearInterval(timer);

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [step, linkUrl]);

  const handleSubmit = async (values: Record<string, any>) => {
    setFormData(values);
    sendButtonClickEvent({
      buttonName: isCheckout
        ? ButtonName.SendCheckoutLink
        : ButtonName.SendApplyLink,
    });

    if (!isCheckout && !userPermissions.ApplyExecute) {
      updateCurrentModal('applyDisabled');
      return;
    }

    if (isCheckout && !userPermissions.CheckoutExecute) {
      updateCurrentModal('checkoutDisabled');
      return;
    }

    setIsLoading(true);
    try {
      const { isCheckout, settleSelection, ...sendLinkData } = values;
      const result = await sendLink({
        ...sendLinkData,
        deliveryMethod: linkInteraction.deliveryMethod,
      });
      if (result?.success) {
        if (isQrCode && result?.url) {
          setLinkUrl(result?.url);
          setStep(ELinkModalStep.ViewQr);
          setTimeLeftInSeconds(linkExpirationInSeconds);
        }

        if (!isQrCode) {
          setSentSuccessfully(true);
          setStep(ELinkModalStep.LinkSent);
          startOver();
        }
      } else {
        setSentSuccessfully(false);
        setStep(ELinkModalStep.LinkSent);
        handleLinkError(updateError, result?.error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = () => {
    setError(null);
    formData && handleSubmit(formData);
  };

  const onSendEmail = async () => {
    updateLinkInteraction({
      ...linkInteraction,
      deliveryMethod: LinkDeliveryMethod.Email,
    });

    const result = await sendLink({
      ...formData,
      deliveryMethod: LinkDeliveryMethod.Email,
    });

    if (result?.success) {
      setSentSuccessfully(true);
      startOver();
    } else {
      setSentSuccessfully(false);
      handleLinkError(updateError, result?.error);
    }
    setStep(ELinkModalStep.LinkSent);
  };

  const onReturnToDetails = () => {
    setError(null);
    setStep(ELinkModalStep.GetDetails);
  };

  return (
    <Box>
      <StyledDialogTitle color="primary" data-testid="link-modal-title">
        {getModalHeader(
          step,
          linkInteraction,
          sentSuccessfully,
          citiExperience,
        )}
      </StyledDialogTitle>
      {step === ELinkModalStep.GetDetails && !citiExperience && (
        <ToggleWrapper>
          <VerticalSpace units={1} />
          <ToggleGroup
            data-testid="delivery-method-toggle"
            disabled={interactionOptions.length === 1}
            fullWidth
            options={interactionOptions}
            value={linkInteraction.deliveryMethod}
            onChange={updateSelectedDeliveryMethod}
          />
        </ToggleWrapper>
      )}
      <Form
        initialValues={{
          ...consumerDetails,
          isCheckout,
          settleSelection: selectedSettleOption === 'AutomaticAndManual',
        }}
        onSubmit={handleSubmit}
        validationSchema={sendLinkFormSchema(
          mobileValidationRegexByCurrency(currency),
          linkInteraction.deliveryMethod,
        )}
        render={({ submitting, invalid }) => (
          <>
            <Box px={3} py={1} minWidth={(theme) => theme.spacing(100)}>
              {step === ELinkModalStep.ViewQr ? (
                <QrCodeScreen
                  link={linkUrl}
                  linkInteraction={linkInteraction}
                  onRefresh={onRefresh}
                  onSendEmail={onSendEmail}
                  timeLeft={timeLeftInSeconds}
                />
              ) : step === ELinkModalStep.ExpiredLink ? (
                <ExpiredLink close={close} />
              ) : step === ELinkModalStep.LinkSent ? (
                <LinkSent
                  error={error}
                  onTryAgain={onRefresh}
                  returnToCustomerData={onReturnToDetails}
                  linkInteraction={linkInteraction}
                  close={close}
                  deliveryTarget={
                    linkInteraction.deliveryMethod !== LinkDeliveryMethod.Sms
                      ? formData?.email
                      : formData?.mobilePhoneNumber
                  }
                  success={sentSuccessfully}
                />
              ) : (
                <ConsumerLinkDetails
                  invalid={invalid}
                  submitting={submitting}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </>
        )}
      />
    </Box>
  );
};
export default LinkModal;

const getModalHeader = (
  step: ELinkModalStep,
  interaction: LinkInteraction,
  success: boolean,
  citiExperience: boolean = false,
) => {
  const flowType = interaction.action === LinkFlow.Apply ? 'apply' : 'checkout';

  switch (step) {
    case ELinkModalStep.GetDetails:
      return citiExperience
        ? `${ESendLinkModal.Generate} ${flowType} ${ESendLinkModal.QrCode}`
        : ESendLinkModal.CustomerInformationTitle;

    case ELinkModalStep.ViewQr:
      return `Scan qr code to start ${flowType} flow`;
    case ELinkModalStep.ExpiredLink:
      return `Link expired`;
    case ELinkModalStep.LinkSent:
      if (citiExperience)
        return `Send ${flowType} Link VIA ${interaction.deliveryMethod}`;
      if (success) return `Link sent via ${interaction.deliveryMethod}`;
      else {
        if (interaction.deliveryMethod !== LinkDeliveryMethod.QrCode)
          return `Link sent error`;
        else return `Qr Code failed`;
      }
    default:
      return '';
  }
};

const getInteractionOptions = (
  merchantInteractionOptions: Record<string, boolean>,
  flow: LinkFlow,
) => {
  const interactions = [];

  if (merchantInteractionOptions[`${flow}Link`]) {
    interactions.push({
      label: 'Email',
      value: LinkDeliveryMethod.Email.toString(),
    });
  }

  if (merchantInteractionOptions[`${flow}Sms`]) {
    interactions.push({
      label: 'SMS',
      value: LinkDeliveryMethod.Sms.toString(),
    });
  }

  if (merchantInteractionOptions[`${flow}QrCode`]) {
    interactions.push({
      label: 'Qr Code',
      value: LinkDeliveryMethod.QrCode.toString(),
    });
  }

  return interactions;
};

const mapLinkErrorToMessage = (error?: string): LinkError => {
  switch (error) {
    case '10201':
      return {
        code: error,
        message: (
          <>
            The customer has opted out of receiving emails.
            <br />
            Please contact the customer to get another email address and try
            again.
          </>
        ),
      };

    default:
      return null;
  }
};

const handleLinkError = (
  updateError: (v: LinkError) => void,
  error?: string,
) => {
  const errorMessages = mapLinkErrorToMessage(error);
  if (errorMessages) updateError(errorMessages);
};
